<template>
	<div>
		<b-row class="mb-2">
			<b-col>
				<b-row>
					<b-col class="d-flex justify-content-end align-items-center pb-3" cols="12">
						<div class="wrap-status d-flex justify-content-center">
							<div class="d-flex">
								<div v-if="invoice.allExpenseItemIsInContract === false" class="d-flex justify-content-start align-items-center">
									<button class="btn-transparent text-color-rhapsody-in-blue" v-b-tooltip.left.html :title="tooltipInvoiceOutsideContract()">
										<component :is="getLucideIcon('AlertTriangle')" color="#EA4E" :size="18" />
									</button>
								</div>
							</div>
							<div :id="`popover-refused-${invoice.id}`" :class="`status ${invoice.statusClass}`" style="font-size: 0.85rem; font-weight: 700">
								{{ invoice.validatedStatus }}
							</div>
						</div>
					</b-col>
					<b-col cols="6" sm="6" md="6" lg="3" xl="3">
						<b-form-group :label="FormMSG(1, 'Invoice date')">
							<v-date-picker v-model="$v.invoice.date.$model">
								<template #default="{ inputValue, togglePopover }">
									<b-input-group class="w-100">
										<b-form-input
											:value="inputValue"
											class="form-control"
											style="border-radius: 10px 0 0 10px"
											:placeholder="FormMSG(2, 'DD/MM/YYYY')"
											:disabled="!canEdit"
											readonly
											@click="togglePopover()"
										/>
										<b-input-group-append style="cursor: pointer">
											<b-input-group-text class="input-group-text bgc-grey-light" @click="togglePopover()">
												<component :is="getLucideIcon('Calendar')" :size="20" :stroke-width="2" />
											</b-input-group-text>
										</b-input-group-append>
									</b-input-group>
								</template>
							</v-date-picker>
							<div v-if="$v.invoice.date.$error" class="invalid-feedback">
								{{ FormMSG(30, 'Invoice date is required') }}
							</div>
						</b-form-group>
					</b-col>
					<b-col cols="6" sm="6" md="6" lg="3" xl="3">
						<b-form-group :label="FormMSG(47, 'Date')">
							<b-form-input :value="invoiceCreatedAt" disabled />
						</b-form-group>
					</b-col>
					<b-col cols="12" sm="12" md="12" lg="3" xl="3">
						<department-selection
							:edit-data="invoice"
							:form-type="invoiceFormType"
							remove-dep-zero
							department-desktop-size="12"
							category-desktop-size="0"
							:is-submitted="isSubmitted"
							hide-category
							hide-transportation-mean
							:is-new="false"
							:disabled="!canEdit"
							@change="(e) => handleDepartmentChange(e)"
							for-expense
						/>
					</b-col>
					<b-col cols="12" sm="12" md="12" lg="3" xl="3" class="d-flex align-items-center" :class="maxMobileScreenWidth ? 'mb-2' : ''">
						<b-form-checkbox size="lg" v-model="invoice.toRefund" :disabled="!canEdit" />
						<span
							>{{ FormMSG(68, 'To refund to') }} <strong>{{ invoice.user.firstName + ' ' + invoice.user.name }}</strong>
						</span>
					</b-col>
				</b-row>
				<b-row class="align-items-start">
					<b-col cols="12" sm="12" md="6" :class="{ 'mb-2': maxMobileScreenWidth }">
						<supplier-selector
							:title="`${!isSupplierUpdate ? FormMSG(28, 'Add supplier details') : FormMSG(29, 'Update supplier details')}`"
							:label="FormMSG(5, 'Supplier')"
							:placeholder="FormMSG(6, 'select supplier here')"
							:supplier-id="$v.invoice.supplierId.$model"
							:ocr-suppliers="suppliersInImages"
							@supplier:unselected="onUnselected"
							@change="handleSupplierChange"
							:disabled="!canEdit"
							:map-disabled="!canEdit"
							:hide-action="!canEdit"
							use-new-design
							version="1.0"
							show-vat-number
						/>
						<div v-if="$v.invoice.supplierId.$error" class="invalid-feedback">
							{{ FormMSG(32, 'Invoice supplier name is required and must be valid') }}
						</div>
					</b-col>
					<b-col cols="6" sm="6" md="3">
						<b-form-group :label="FormMSG(7, 'Invoice number')">
							<template slot="label">
								<label class="pt-1" for="invoiceNumber">{{ FormMSG(7, 'Invoice number') }}</label>
							</template>
							<b-form-input v-model="$v.invoice.invoiceNumber.$model" placeholder="0" :disabled="!canEdit" />
							<div v-if="$v.invoice.invoiceNumber.$error" class="invalid-feedback">
								{{ FormMSG(33, 'Invoice number is required and must be valid') }}
							</div>
						</b-form-group>
					</b-col>
					<b-col cols="6" sm="6" md="3">
						<b-form-group class="flex-grow-1">
							<template slot="label">
								<label class="pt-1" for="invoiceNumber">{{ FormMSG(8, 'Total amount') }}</label>
							</template>
							<b-input-group>
								<b-form-input
									v-model="$v.invoice.amountTotal.$model"
									type="number"
									step="0.01"
									min="0"
									placeholder="0"
									lazy
									lazy-formatter
									number
									:formatter="roundNumeric"
									:disabled="!canEdit"
								/>
								<b-input-group-append>
									<b-input-group-text>
										<currency-svg color="#06263E" opacity="0.85" width="15" height="15" />
									</b-input-group-text>
								</b-input-group-append>
							</b-input-group>
							<div v-if="$v.invoice.amountTotal.$error" class="invalid-feedback">
								{{ FormMSG(34, 'Total amount should not be empty') }}
							</div>
						</b-form-group>
					</b-col>
				</b-row>
				<b-row>
					<b-col cols="12" sm="12" md="6">
						<po-selector
							id="po-selector-final-form"
							ref="po-selector-final-form"
							:selected-po-data="invoice.selectedPo"
							:is-disabled="!canEdit"
							@change:poList="(poID, newData) => changeSelectedPoList(poID, newData)"
							@click:more="showPoDialog(true)"
						>
						</po-selector>
					</b-col>
					<b-col cols="12" sm="12" md="6" v-if="invoice.filesData.length > 0 || canEdit">
						<b-form-group :label="`${FormMSG(11, 'Attachements')}${invoice.filesData.length ? ` (${invoice.filesData.length})` : ''}`">
							<div
								class="w-100 rounded-lg border"
								style="padding: 2px 0 2px 0; border-radius: 8px !important; border: 1px solid #cad0d7 !important"
							>
								<template>
									<b-row class="p-2" v-for="(fileObj, index) in invoice.filesData" :key="index">
										<b-col class="d-flex justify-content-between align-items-center" style="margin-bottom: -16px">
											<li class="list-file w-90">
												<span>{{ fileObj.file.name }}</span>
											</li>
											<div class="d-flex align-items-center">
												<b-button
													size="sm"
													variant="link"
													v-b-tooltip.hover.viewport="FormMSG(46, 'View details')"
													@click="previewFile(fileObj)"
												>
													<component :is="getLucideIcon('File')" :size="20" color="#59c1bf" :stroke-width="2" />
												</b-button>
												<b-button
													size="sm"
													variant="link"
													v-b-tooltip.hover.viewport="FormMSG(27, 'Remove file')"
													@click="removeFile(fileObj, index)"
													v-if="!isFileTypeInvoice(fileObj) && canEdit"
												>
													<component :is="getLucideIcon('X')" :size="20" color="#999999" :stroke-width="3" />
												</b-button>
											</div>
										</b-col>
									</b-row>
								</template>
								<b-row :style="{ 'margin-top': invoice.filesData.length > 0 ? '12px' : '0px' }">
									<b-col
										class="d-flex justify-content-end align-items-center form-control-sm"
										:class="{ 'text-muted': !canEdit }"
										style="gap: 8px; margin-right: 16px"
										:style="{ cursor: canEdit ? 'pointer' : 'default' }"
										@click="canEdit ? openFileUploader() : () => {}"
									>
										<div style="margin-top: 4px">{{ FormMSG(12, 'Add / edit attachements') }}</div>
										<div>
											<component
												:is="getLucideIcon('PlusCircle')"
												:color="canEdit ? '#00A09C' : '#59c1bf'"
												:size="18"
												:stroke-width="2.5"
											/>
										</div>
									</b-col>
								</b-row>
							</div>
						</b-form-group>
					</b-col>
				</b-row>
			</b-col>
		</b-row>
		<b-row>
			<b-col>
				<b-row class="d-flex align-items-center" :class="maxMobileScreenWidth ? 'mb-3' : ''">
					<b-col cols="12" sm="12" md="6">
						<b-form-group :label="FormMSG(13, 'Summary description')">
							<b-form-textarea
								class="description-overflow"
								:placeholder="FormMSG(14, 'Add description here ...')"
								v-model="$v.invoice.description.$model"
								:disabled="!canEdit"
								:rows="2"
							/>
							<div v-if="$v.invoice.description.$error" class="invalid-feedback">
								{{ FormMSG(36, 'A description is required') }}
							</div>
						</b-form-group>
					</b-col>
					<b-col cols="12" sm="12" md="6" class="d-flex align-items-center" v-if="canEdit && !initialItemsMerged">
						<b-button size="sm" variant="secondary" class="px-3" @click="handleMergedItems" :disabled="itemsOperationInProcess">
							<span v-if="itemsOperationInProcess">
								<b-spinner small />
								{{ FormMSG(59, 'Loading ...') }}
							</span>
							<span v-else-if="invoice.itemsMerged">
								<component :is="getLucideIcon('RefreshCw')" :size="18" color="#fff" :stroke-width="2.5" class="mr-2" />
								{{ FormMSG(57, 'Restore details') }}
							</span>
							<span v-else>
								<component :is="getLucideIcon('Shrink')" :size="18" color="#fff" :stroke-width="2.5" class="mr-2" />
								{{ FormMSG(58, 'Merge details') }}
							</span>
						</b-button>
					</b-col>
				</b-row>
				<b-row>
					<b-col>
						<div class="fixed">
							<transition tag="div" name="floating-wizard" appear>
								<expense-item-column-wizard
									v-if="showContextWizard"
									:wizard-position-x="wizardPositionX"
									:wizard-position-y="wizardPositionY"
									:tax-options="taxOptionsInvoice"
									:custom-flags="customFlagsInvoice"
									:all-categories="allInvoiceCategories"
									:selection-data="selectionData"
									:selected-items-count="selectedItems.length"
									@wizard:setSelectionDataCostCode="(e) => setSelectionDataCostCode(e)"
									@wizard:setSelectionDataTaxCode="(e) => setSelectionDataTaxCode(e)"
									@wizard:setSelectionDataCustomFlags="(e, flag) => setSelectionDataCustomFlags(e, flag)"
									@wizard:clearSelection="clearSelection"
									@wizard:computeSelection="computeAllSelectedRows"
								/>
							</transition>
							<b-table
								id="table-clickable-invoice"
								ref="invoiceItemsTable"
								:hover="hover"
								responsive="sm"
								:items="provideItemsTableData"
								:fields="invoiceItemsFields"
								:current-page="currentPage"
								:busy.sync="itemsTableBusy"
								:per-page="0"
								bordered
								outlined
								small
								caption-top
								selectable
								select-mode="range"
								:sticky-header="$screen.height / 2 - 120 + 'px'"
								:head-row-variant="hv"
								:table-class="{ 'mini-table': maxMobileScreenWidth, 'table-no-outline-border': true }"
								:tbody-tr-class="{ 'tr-cursor': canEdit }"
								thead-class="custom-sticky-header z-index-3000"
								tfoot-class="custom-sticky-footer"
								:empty-text="FormMSG(15, 'No item added yet')"
								show-empty
								@row-clicked="(e, i) => (maxMobileScreenWidth ? itemRowClicked(e, i) : {})"
								@row-dblclicked="(e, i) => (!maxMobileScreenWidth ? itemRowClicked(e, i) : {})"
								@row-selected="(e) => (selectedItems = e)"
								@row-contextmenu="(item, index, event) => (!maxMobileScreenWidth ? openContextWizard(item, index, event) : {})"
							>
								<template #table-busy v-if="!itemsTableRenderOnce">
									<div class="d-flex justify-content-center align-items-center">
										<b-spinner class="align-middle" small></b-spinner>
										<strong class="ml-2">{{ FormMSG(59, 'Loading') }}...</strong>
									</div>
								</template>
								<template #thead-top v-if="!maxMobileScreenWidth">
									<b-tr>
										<b-th class="bg-white border-0" colspan="8"></b-th>
										<b-th :colspan="customFlagKeys.length" class="bg-white border-0" v-if="customFlagKeys.length > 0">
											<div class="w-100 rounded-pill small bg-primary text-white header-budget-col p-0">
												{{ FormMSG(38, 'Flags') }}
											</div>
										</b-th>
										<b-th class="bg-white border-0"></b-th>
									</b-tr>
								</template>
								<template #head(costCenter)="data">
									<expense-item-data-propagation
										:data="data"
										:can-edit="canEdit"
										triggerID="cost-center-setup"
										custom-class="z-index-4000"
										:all-categories="allInvoiceCategories"
										@computePropagation="computeCostCodeForAll"
										is-column-cost-code
										expand-menu
									></expense-item-data-propagation>
								</template>
								<template #head(tax)="data">
									<expense-item-data-propagation
										:data="data"
										:can-edit="canEdit"
										triggerID="tax-code-setup"
										custom-class="z-index-4000"
										:tax-options="taxOptionsInvoice"
										@computePropagation="computeTaxCodeForAll"
										is-column-tax-code
										expand-menu
									/>
								</template>
								<template v-for="(flag, flagIndex) in customFlagKeys" #[`head(${flag.key})`]="data">
									<div :key="flagIndex">
										<expense-item-data-propagation
											:key="flagIndex"
											:data="data"
											:can-edit="canEdit"
											:triggerID="`flags-setup-${flag.key}`"
											custom-class="z-index-4000"
											:project-flag-item="customFlagsInvoice[flagIndex].projectFlagItem"
											@computePropagation="computeCustomFlagForAll"
											is-column-custom-flag
											expand-menu
										/>
									</div>
								</template>
								<template #cell(description)="{ item, index }">
									<span :id="`invoice-description-${index}`" class="text-description-overflow-ellypsis">
										<b-tooltip
											custom-class="invoices-description-tooltip"
											:target="`invoice-description-${index}`"
											triggers="hover"
											placement="left"
										>
											{{ item.description }}
										</b-tooltip>
										{{ item.description }}
									</span>
								</template>
								<template #cell(poId)="{ item }">
									<div class="d-flex align-items-center justify-content-between" v-if="item.poId && item.poId != 0">
										<span :class="isNewCreation ? 'ml-3 w-50' : 'w-100'">{{ item.poId }}</span>
										<span class="px-2 w-25" style="color: #a1a1aa" v-if="isNewCreation">{{ provideActions(item.action) }}</span>
									</div>
								</template>
								<template #cell(costCenter)="{ item, index }">
									<div style="width: 100%; height: 100%; z-index: 1" @click.stop>
										<cost-center-select
											extend-menu
											:all-categories="allInvoiceCategories"
											:edit-data="item"
											:is-disabled="!canEdit"
											@change="(costCenterItem) => handleLineCategoryChange(costCenterItem, index)"
											@click.stop
										/>
									</div>
								</template>
								<template v-for="(flag, index) in customFlagKeys" v-slot:[`cell(${flag.key})`]="{ item, index: rowIndex }">
									<div :key="`cell-${flag.key}-${rowIndex}`" style="width: 100%; height: 100%; z-index: 1" @click.stop>
										<v-select
											class="w-100 custom-flag-select-class"
											:key="`select-${flag.key}-${rowIndex}`"
											label="name"
											placeholder="..."
											:disabled="!canEdit"
											:value="
												item.projectFlagItems.customFlags.find((flag) =>
													customFlagsInvoice[index].projectFlagItem.some((item) => +item.id === +flag.id)
												)
											"
											:options="customFlagsInvoice[index].projectFlagItem"
											:reduce="(option) => option.id"
											@input="(flagItem) => handleLineCustomFlagChange(flagItem, customFlagsInvoice[index], rowIndex)"
											appendToBody
										/>
									</div>
								</template>
								<template #cell(amountTotal)="{ item }">
									<span :class="item.amountTotal > 0 ? '' : 'text-red'"> {{ setCurrency(item.amountTotal) }}</span>
								</template>
								<template #cell(actionColumn)="{ item, index }">
									<b-button variant="link" class="text-danger" @click.stop="showConfirmRowDelete(item, index)">
										<component :is="getLucideIcon('Trash')" :size="16" :stroke-width="2" />
									</b-button>
								</template>
								<template #custom-foot>
									<b-tr>
										<b-th :colspan="maxMobileScreenWidth ? '1' : '4'" class="text-right vertical-centered">{{
											FormMSG(37, 'Totals')
										}}</b-th>
										<b-th class="text-right font-weight-normal vertical-centered">{{ setCurrency(itemsNetTotal) }}</b-th>
										<b-th
											:colspan="maxMobileScreenWidth ? '1' : '2'"
											class="text-right font-weight-normal bgc-grey-light vertical-centered"
											>{{ setCurrency(itemsVatTotal) }}</b-th
										>
										<b-th class="text-right bgc-amount-total vertical-centered" :class="{ 'warning-invoice': showAmountWarning }">
											<component
												:is="getLucideIcon('AlertCircle')"
												:size="maxMobileScreenWidth ? 16 : 24"
												:stroke-width="maxMobileScreenWidth ? 2 : 1.5"
												class="mr-3"
												v-if="showAmountWarning"
											/>
											{{ setCurrency(itemsTotalAmount) }}
										</b-th>
										<b-th :colspan="customFlagKeys.length" v-if="!maxMobileScreenWidth && customFlagKeys.length > 0"></b-th>
									</b-tr>
								</template>
							</b-table>
						</div>
					</b-col>
				</b-row>
				<b-row class="mt-2 justify-content-between" v-if="canEdit">
					<b-col sm="12" md="6" :class="{ 'order-2': maxMobileScreenWidth }">
						<b-button size="sm" variant="outline-primary" @click="showLineItemDialog()">
							<span>{{ FormMSG(26, 'Insert line') }}</span>
						</b-button>
					</b-col>
					<!-- <b-col sm="12" md="6" class="d-flex" :class="maxMobileScreenWidth ? 'order-1 justify-content-start mb-2' : 'justify-content-end'">
						<invoice-alert v-if="canSeeError" :maxMobileScreenWidth="maxMobileScreenWidth">
							{{ invoiceFormDialogError }}
						</invoice-alert>
					</b-col> -->
				</b-row>
			</b-col>
		</b-row>
		<dialog-container
			ref="attachementDialog"
			:show="openAttachementDialog"
			:dialogTitle="FormMSG(43, 'Attach')"
			:btnCloseLabel="dialogCancel"
			:btnSaveLabel="dialogSave"
			size="lg"
			centered
			:enableNextOrSave="enableSaveAttachement"
			@close="closeAttachementDialog"
			@nextOrSave="saveAttachements"
		>
			<template #content>
				<div :class="{ 'mt-4 mx-3': maxMobileScreenWidth, 'mx-5': !maxMobileScreenWidth }">
					<files-uploader
						:files-list="newAttachementList"
						:for-single="true"
						@add="onNewAttachements"
						@remove="onRemoveNewAttachement"
						is-from-final-form
					/>
				</div>
			</template>
		</dialog-container>
		<dialog-container
			ref="poDialog"
			:show="openPoDialog"
			:dialogTitle="poDialogInfo.title"
			:btnCloseLabel="poDialogInfo.closeLabel"
			:btnSaveLabel="poDialogInfo.saveLabel"
			:enableNextOrSave="enableSaveInPoModal"
			size="xl"
			@nextOrSave="nextOrSaveProcessPo"
			@close="cancelOrCloseProcessPo"
		>
			<template #content>
				<div :class="$screen.width <= 992 ? 'po-mobile-container pt-4' : 'fixed-height-container'" v-if="showPoDrawDown">
					<po-draw-down-data-view :selected-po="invoice.selectedPo" @selection:change="(e) => updateSelectedPoList(e)" />
				</div>
				<div :class="$screen.width <= 992 ? 'po-mobile-container pt-4' : 'fixed-height-container'" v-if="showPoTransaction">
					<po-transaction-data-view
						:selected-po="tempPoList"
						:selected-items="tempPoItemsList"
						:invoice-date="invoice.date"
						@enable:continue="enableContinuePoTransaction"
						@selection:change="(e) => updateSelectedItems(e)"
					/>
				</div>
			</template>
		</dialog-container>
		<dialog-container
			ref="lineItemDialog"
			:show="openNewLineDialog"
			:show-error="showlineItemDialogError"
			:dialogTitle="lineItemsDialogInfo.title"
			:btnCloseLabel="lineItemsDialogInfo.closeLabel"
			:btnSaveLabel="lineItemsDialogInfo.saveLabel"
			:enableNextOrSave="enableSaveLineItem"
			size="xl"
			@nextOrSave="saveUpdateLineItems"
			@close="closeLineItemView"
		>
			<template #content>
				<line-item-data-view
					ref="lineItemDataView"
					:invoice="invoice"
					:department="invoice.department"
					:custom-flags="customFlagsInvoice"
					:tax-options-props="taxOptionsInvoice"
					:all-categories-props="allInvoiceCategories"
					:line-item-data="currentLineItem"
					:is-not-splittable="isNotSplittableItem"
					@enable:error="(e, n) => showLineErrorMessage(e, n)"
					@enable:save="(e) => (enableSaveLineItem = e)"
				/>
			</template>
			<template #error-message>
				<span>{{ errorLinesAmountExceeds }}</span>
			</template>
		</dialog-container>
	</div>
</template>

<script>
// mixin
import globalMixin from '@/mixins/global.mixin';
import CarbonMenuMixin from '@/components/Budget/carbon.menu.mixin';
import languageMessages from '@/mixins/languageMessages';
import invoicesToolsMixin from '@/mixins/invoicesTools.mixin';
import { validationMixin } from 'vuelidate';
import { required, decimal, minLength } from 'vuelidate/lib/validators';
import expensesFlagsMixin from '@/mixins/expensesFlags.mixin';
// components
import CustomFormGroup from '@/components/Forms/CustomFormGroup';
import DatePicker from 'v-calendar/lib/components/date-picker.umd';
import PoSelector from './PoSelector';
import PoDrawDownDataView from './PoDrawDownDataView';
import PoTransactionDataView from './PoTransactionDataView';
import SupplierSelector from '@/components/SupplierSelector';
import departmentSelection from '@/components/DepartmentsSelection';
import DialogContainer from './DialogContainer';
import FilesUploader from './FilesUploader';
import Treeselect from '@riophae/vue-treeselect';
import CostCenterSelect from './CostCodeSelect';
import ExpenseItemColumnWizard from './ExpenseItemColumnWizard';
import ExpenseItemDataPropagation from './ExpenseItemDataPropagation';
import LineItemDataView from './LineItemDataView';
import CurrencySvg from '@/components/Svg/Currency';
import InvoiceAlert from '@/views/myinvoices/components/InvoiceAlert';
// props
import mapProps from '@/shared/vuePropsMapper';

// utils
import { provideFileDataToUpload } from '@/components/Packages/Captures/capture.utils';
import { isNil, generateSecureId } from '@/shared/utils';
import { rendCurrency, formatTableDate } from '~helpers';
import { store } from '@/store';
import _ from 'lodash';

// constants
import {
	INVOICE_DATA_STRUCT,
	ERROR_STRING,
	INVOICE_ITEMS_ACTION,
	VALIDATION_TYPE,
	INVOICE_ATTACHEMENTS_TYPE,
	INVOICE_ITEMS_OPERATION,
	EXPENSE_TYPE
} from '@/shared/constants';

const DEFAULT_SELECTION_DATA = {
	costCode: {
		category: -1,
		costCenter: '',
		categoryName: ''
	},
	taxeFlags: [],
	customFlags: []
};

// services
import { restoreExpenseItemsOfExpense, mergeExpenseItemsOfExpense } from '@/cruds/expense.crud';

export default {
	name: 'InvoicesForm',
	mixins: [languageMessages, globalMixin, validationMixin, invoicesToolsMixin, CarbonMenuMixin, expensesFlagsMixin],
	components: {
		CustomFormGroup,
		'v-date-picker': DatePicker,
		PoSelector,
		SupplierSelector,
		departmentSelection,
		DialogContainer,
		FilesUploader,
		Treeselect,
		CostCenterSelect,
		ExpenseItemColumnWizard,
		ExpenseItemDataPropagation,
		CurrencySvg,
		PoDrawDownDataView,
		PoTransactionDataView,
		LineItemDataView,
		InvoiceAlert
	},
	props: {
		...mapProps(['hover', 'striped', 'bordered'], {
			type: Boolean,
			default: true
		}),
		...mapProps(['small', 'fixed'], {
			type: Boolean,
			default: false
		}),
		invoiceData: {
			type: Object,
			default: () => {}
		},
		canEdit: {
			type: Boolean,
			default: true
		},
		canDelete: {
			type: Boolean,
			required: false,
			default: true
		},
		isNewCreation: {
			type: Boolean,
			required: false,
			default: false
		},
		taxOptionsInvoice: {
			type: Array,
			default: () => []
		},
		customFlagsInvoice: {
			type: Array,
			default: () => []
		},
		allInvoiceCategories: {
			type: Array,
			default: () => []
		},
		initialOperation: {
			type: Number,
			default: 0
		}
	},
	data() {
		return {
			invoice: _.cloneDeep(INVOICE_DATA_STRUCT),
			changedItemsOperation: 0,
			isItemsOperationChanged: false,
			itemsOperationInProcess: false,
			currentPage: 1,
			hv: 'dark',
			isSubmitted: false,
			invoiceFormType: 1,
			suppliersInImages: [],
			// invoice file data
			openAttachementDialog: false,
			newAttachementList: [],
			invoiceParentType: 'expense',
			invoiceParentSubType: 'invoice',
			afterAttachementUpdate: false,
			// flags
			afterLineItemUpdate: false,
			flags: [],
			customFlagKeys: [],
			// delete row data
			itemToDelete: {},
			itemToDeleteIndex: null,
			// for row selection
			wizardPositionY: 0,
			wizardPositionX: 0,
			showContextWizard: false,
			itemsTableBusy: false,
			itemsTableRenderOnce: false,
			selectedItems: [],
			selectionData: _.cloneDeep(DEFAULT_SELECTION_DATA),
			// for po selections
			poDialogInfo: {
				title: '',
				closeLabel: '',
				saveLabel: ''
			},
			dialogTitlePoDrawdown: '',
			dialogTitlePoTransaction: '',
			dialogTitlePoContinue: '',
			dialogTitlePoNext: '',
			openPoDialog: false,
			showPoDrawDown: true,
			showPoTransaction: false,
			enableTransactionContinueBtn: false,
			tempPoList: [],
			tempPoItemsList: [],
			// for line items dialog
			openNewLineDialog: false,
			lineItemsDialogInfo: {
				title: '',
				closeLabel: '',
				saveLabel: ''
			},
			titleNewLineItem: '',
			titleEditLineItem: '',
			errorLinesAmountExceeds: '',
			currentLineItem: {
				category: 0
			},
			currentLineItemIndex: null,
			isNotSplittableItem: true,
			enableSaveLineItem: false,
			showlineItemDialogError: false,
			// common dialog messages
			dialogCancel: '',
			dialogSave: '',
			errorMessage: '',
			initialItemsMerged: false
		};
	},
	watch: {
		invoiceData: {
			handler(newVal) {
				if (!this.afterLineItemUpdate && !this.afterAttachementUpdate) {
					this.invoice = _.cloneDeep({ ...this.invoice, ...newVal });
					this.initialItemsMerged = this.invoice.itemsMerged;
					if (this.$refs.invoiceItemsTable) this.$refs.invoiceItemsTable.refresh();
				}
				this.afterLineItemUpdate = false;
				this.afterAttachementUpdate = false;
			},
			deep: true,
			immediate: true
		},
		invoice: {
			handler() {
				this.$v.$touch();
				this.$emit('enable:save', !this.$v.$invalid && this.canEdit && !this.showAmountWarning);
			},
			deep: true,
			immediate: true
		},
		showAmountWarning: {
			handler(newVal) {
				this.$emit('enable:error', newVal, 'default');
			},
			immediate: true
		}
	},
	computed: {
		invoiceItemsFields() {
			let fields = [
				{
					key: 'description',
					label: this.FormMSG(17, 'Description'),
					sortable: true,
					class: 'text-left vertical-centered',
					thClass: 'text-center',
					thStyle: { color: '#fff', width: '200px', minWidth: '150px', maxWidth: '200px' },
					tdStyle: { width: '200px', minWidth: '200px', maxWidth: '250px' }
				},
				{
					key: 'amount',
					label: this.FormMSG(20, 'Net amount'),
					formatter: (v) => rendCurrency(v),
					sortable: true,
					class: 'vertical-centered',
					thClass: 'text-center',
					tdClass: 'text-right',
					thStyle: { color: '#fff', 'min-width': this.maxMobileScreenWidth ? '60px' : '100px' }
				},
				{
					key: 'amountVat',
					label: this.FormMSG(22, 'Tax'),
					formatter: (v) => rendCurrency(v),
					sortable: true,
					class: 'vertical-centered',
					thClass: 'text-center',
					tdClass: 'text-right files-btn-background',
					thStyle: { color: '#fff', 'min-width': this.maxMobileScreenWidth ? '50px' : '70px' }
				},
				{
					key: 'amountTotal',
					label: this.FormMSG(23, 'Total'),
					formatter: (v) => rendCurrency(v),
					sortable: true,
					class: 'vertical-centered',
					thClass: 'text-center',
					tdClass: 'text-right bgc-amount-total',
					thStyle: { color: '#fff', 'min-width': this.maxMobileScreenWidth ? '80px' : '120px' }
				}
			];
			const poIdField = {
				key: 'poId',
				label: this.FormMSG(16, 'PO'),
				sortable: true,
				class: 'text-center vertical-centered',
				thStyle: { color: '#fff', 'min-width': '80px' }
			};
			const fieldsAfterDescription = [
				{
					key: 'costCenter',
					label: this.FormMSG(18, 'Cost code'),
					sortable: true,
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff', width: '200px', minWidth: '200px', maxWidth: '200px' },
					tdStyle: { width: '200px', minWidth: '200px', maxWidth: '200px' },
					isSlot: true
				},
				{
					key: 'coTwo',
					label: this.FormMSG(19, 'CO2 type'),
					sortable: true,
					formatter: (value, key, item) => this.getCo2Detail(item.carbon.coTwoCategory),
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff', whiteSpace: 'nowrap' }
				}
			];
			const fieldsAfterNetAmount = [
				{
					key: 'tax',
					label: this.FormMSG(21, 'Tax code'),
					sortable: true,
					formatter: (value, key, item) => {
						//To verify
						if (!_.isUndefined(item.projectFlagItems) && !_.isNil(item.projectFlagItems) && item.projectFlagItems.taxeFlags.length > 0) {
							// return item.projectFlagItems.taxeFlags[0].name + ' ( ' + item.projectFlagItems.taxeFlags[0].factor + '% )';
							return item.projectFlagItems.taxeFlags[0].factor + ' %';
						}
						return '';
					},
					class: 'text-center vertical-centered',
					thStyle: { color: '#fff', 'min-width': '70px' }
				}
			];
			const actionField = {
				key: 'actionColumn',
				label: '',
				class: 'text-center vertical-centered',
				thStyle: { color: '#fff' }
			};

			if (!this.maxMobileScreenWidth) {
				fields.unshift(poIdField);
				const decriptionIndex = fields.findIndex((x) => x.key === 'description');
				fields.splice(decriptionIndex + 1, 0, ...fieldsAfterDescription);

				const netAmountIndex = fields.findIndex((x) => x.key === 'amount');
				fields.splice(netAmountIndex + 1, 0, ...fieldsAfterNetAmount);

				if (this.customFlagKeys.length > 0) {
					fields.splice(fields.length, 0, ...this.customFlagKeys);
				}
			}
			if (this.canEdit && this.canDelete) fields.push(actionField);

			return fields;
		},
		isSupplierUpdate() {
			return !isNil(this.invoice) && parseInt(this.invoice.supplierId, 10) !== 0;
		},
		itemsNetTotal() {
			if (this.invoice.expenseItem.length === 0) return 0;
			const netTotal = this.invoice.expenseItem.reduce((accumulator, current) => accumulator + current.amount, 0);
			return netTotal;
		},
		itemsVatTotal() {
			if (this.invoice.expenseItem.length === 0) return 0;
			const vatTotal = this.invoice.expenseItem.reduce((accumulator, current) => accumulator + current.amountVat, 0);
			return vatTotal;
		},
		itemsTotalAmount() {
			if (this.invoice.expenseItem.length === 0) return 0;
			const amountTotal = this.invoice.expenseItem.reduce((accumulator, current) => {
				if (![INVOICE_ITEMS_ACTION.IGNORE, INVOICE_ITEMS_ACTION.VOID].includes(current.action)) {
					return accumulator + current.amountTotal;
				} else return accumulator;
			}, 0);
			return amountTotal;
		},
		showAmountWarning() {
			const numberedItemsAmount = parseFloat(this.itemsTotalAmount).toFixed(2);
			return isNaN(this.invoice.amountTotal) || parseFloat(this.invoice.amountTotal).toFixed(2) !== numberedItemsAmount;
		},
		isAmountContainZero() {
			return this.invoice.expenseItem.some((obj) => parseFloat(obj.amountTotal) === 0);
		},
		enableSaveAttachement() {
			const missingFileType = this.newAttachementList.find((file) => isNil(file.type));
			return this.newAttachementList.length > 0 && !missingFileType;
		},
		invoiceCreatedAt() {
			return formatTableDate(this.invoice.createdAt);
		},
		enableSaveInPoModal() {
			if (this.showPoDrawDown) {
				return this.tempPoList.length > 0;
			} else if (this.showPoTransaction) {
				return this.enableTransactionContinueBtn;
			}
		}
	},
	created() {
		this.dialogTitlePoDrawdown = this.FormMSG(61, 'PO Drawdown');
		this.dialogTitlePoTransaction = this.FormMSG(62, 'PO Transaction line selection');
		this.dialogTitlePoContinue = this.FormMSG(63, 'Continue');
		this.dialogTitlePoNext = this.FormMSG(64, 'Next');
		this.dialogCancel = this.FormMSG(44, 'Cancel');
		this.dialogSave = this.FormMSG(45, 'Save');
		this.titleNewLineItem = this.FormMSG(65, 'New Transaction line');
		this.titleEditLineItem = this.FormMSG(66, 'Edit Transaction line');
		this.errorLinesAmountExceeds = this.FormMSG(67, 'Splitted lines amount exceeds the initial amount total');
	},
	mounted() {
		this.changedItemsOperation = this.initialOperation;
		this.customFlagKeys = this.flagFields();
		if (this.checkIfInvoiceRejected()) this.$emit('enable:error', true, this.invoice.comment);
	},
	methods: {
		submitProcess() {
			this.invoice.amountTotal = parseFloat(this.invoice.amountTotal);
			this.$emit('submit:form', this.invoice, this.changedItemsOperation);
		},
		isFileTypeInvoice(file) {
			return file.type === INVOICE_ATTACHEMENTS_TYPE.INVOICE;
		},
		async provideItemsTableData() {
			this.itemsTableBusy = true;
			const itemsPromise = await new Promise((resolve) => {
				setTimeout(() => {
					this.itemsTableRenderOnce = true;
					resolve(this.invoice.expenseItem);
				}, 150);
			});
			this.itemsTableBusy = false;
			return itemsPromise;
		},
		setItemsOperationStatus(newItems, newOperation, newStatus) {
			if (!this.isItemsOperationChanged) {
				this.changedItemsOperation = newOperation;
				this.isItemsOperationChanged = true;
			} else {
				this.changedItemsOperation = this.initialOperation;
				this.isItemsOperationChanged = false;
			}

			// remove null result.
			const items = newItems.filter((item) => !_.isNil(item));

			this.invoice.expenseItem = _.cloneDeep(
				items.map((item) => {
					return {
						...item,
						date: this.invoice.date,
						id: +item.id,
						expenseItemId: +item.id,
						amount: _.round(parseFloat(item.amount), 2),
						amountVat: _.round(parseFloat(item.amountVat), 2),
						amountTotal: _.round(parseFloat(item.amountTotal), 2),
						type: EXPENSE_TYPE.INVOICE,
						action: item.action,
						projectFlagItems: {
							customFlags: [],
							taxeFlags: []
						}
					};
				})
			);

			this.invoice.itemsMerged = newStatus;
		},
		async restoreOrMergeItems() {
			this.itemsOperationInProcess = true;
			if (this.invoice.itemsMerged) {
				try {
					const newItems = await restoreExpenseItemsOfExpense(+this.invoice.id);
					this.setItemsOperationStatus(newItems, INVOICE_ITEMS_OPERATION.SPLIT, false);
				} catch (error) {
					console.log('error on fetching retored Items', error);
				}
			} else {
				try {
					const expenseInput = _.omit(this.invoice, ['documents', 'filesData', 'selectedPo']);
					expenseInput.id = parseInt(expenseInput.id, 10);
					expenseInput.amountTotal = parseFloat(expenseInput.amountTotal);
					const newItems = await mergeExpenseItemsOfExpense(expenseInput.id, expenseInput);
					this.setItemsOperationStatus([newItems], INVOICE_ITEMS_OPERATION.MERGE, true);
				} catch (error) {
					console.log('error on fetching merged Items', error);
				}
			}
			this.$refs.invoiceItemsTable.refresh();
			this.itemsOperationInProcess = false;
		},
		computeCostCodeForAll(globalCostCode) {
			this.invoice.expenseItem = _.cloneDeep(
				this.invoice.expenseItem.map((item) => {
					return {
						...item,
						category: globalCostCode.category,
						costCenter: globalCostCode.costCenter,
						categoryName: globalCostCode.categoryName
					};
				})
			);
			this.$refs.invoiceItemsTable.refresh();
		},
		computeTaxCodeForAll(globalTaxCode) {
			if (globalTaxCode.length > 0) {
				this.invoice.expenseItem = _.cloneDeep(
					this.invoice.expenseItem.map((item) => {
						const { newVat, newTotal } = this.computeSelectionPrice(item, globalTaxCode);
						const newCustomFlags = this.computeSelectionFlags(item.projectFlagItems.customFlags, globalTaxCode);
						return {
							...item,
							amountVat: newVat,
							amountTotal: newTotal,
							projectFlagItems: {
								taxeFlags: globalTaxCode,
								customFlags: newCustomFlags
							}
						};
					})
				);
			}
			this.$refs.invoiceItemsTable.refresh();
		},
		computeCustomFlagForAll(globalCustomFlag) {
			this.invoice.expenseItem = _.cloneDeep(
				this.invoice.expenseItem.map((item) => {
					const newCustomFlags = this.computeSelectionFlags(item.projectFlagItems.customFlags, [globalCustomFlag]);
					return {
						...item,
						projectFlagItems: {
							...item.projectFlagItems,
							customFlags: newCustomFlags
						}
					};
				})
			);
			this.$refs.invoiceItemsTable.refresh();
		},
		clearSelection() {
			this.selectionData = _.cloneDeep(DEFAULT_SELECTION_DATA);
			this.selectedItems = [];
			this.$refs.invoiceItemsTable.clearSelected();
			this.showContextWizard = false;
		},
		setSelectionDataCostCode(payload) {
			if (payload.costcodeText) {
				this.selectionData.costCode = _.cloneDeep({
					category: +payload.category,
					costCenter: payload.costcodeText,
					categoryName: payload.categoryText
				});
			}
		},
		setSelectionDataTaxCode(payload) {
			let tax = this.taxOptionsInvoice.filter((tax) => +payload === +tax.value)[0];
			let taxIds = this.taxOptionsInvoice.map((taxes) => taxes.value);

			if (!_.isNil(payload)) {
				if (this.selectionData.taxeFlags.length > 0) {
					const cleanCustomFlags = this.selectionData.customFlags.filter((flag) => !taxIds.includes(flag.id));
					cleanCustomFlags.push(tax.item);
					this.selectionData.customFlags = cleanCustomFlags;
				} else this.selectionData.customFlags.push(tax.item);
				this.selectionData.taxeFlags[0] = tax.item;
			} else {
				// Remove the tax item from the custom flag array
				this.selectionData.customFlags = this.selectionData.customFlags.filter((flag) => !taxIds.includes(flag.id));
				this.selectionData.taxeFlags = [];
			}
		},
		setSelectionDataCustomFlags(payload, currentFlag) {
			const newItem = currentFlag.projectFlagItem.filter((item) => +item.id === +payload)[0];
			const index = this.selectionData.customFlags.findIndex((selectedFlag) => selectedFlag.flagId === currentFlag.flagId);
			if (index === -1) {
				this.selectionData.customFlags.push(newItem);
			} else {
				if (_.isNil(payload)) {
					this.selectionData.customFlags.splice(index, 1);
				} else {
					this.selectionData.customFlags.splice(index, 0, newItem);
				}
			}
		},
		isItemSelected(item) {
			const selectedIds = this.selectedItems.map((x) => +x.id);
			const selectedDesc = this.selectedItems.map((x) => x.description.toUpperCase());
			return selectedIds.includes(+item.id) && selectedDesc.includes(item.description.toUpperCase());
		},
		computeSelectionPrice(item, taxeFlag) {
			const newVat = parseFloat((parseFloat(taxeFlag[0].factor / 100) * parseFloat(item.amount)).toFixed(2));
			const newTotal = parseFloat(parseFloat(item.amount + newVat).toFixed(2));
			return { newVat, newTotal };
		},
		computeSelectionFlags(currentFlags, selectedFlags) {
			const newSelectionFlags = [];
			const selectedFlagsIDs = selectedFlags.map((x) => +x.flagId);
			currentFlags.forEach((element) => {
				if (!selectedFlagsIDs.includes(+element.flagId)) {
					newSelectionFlags.push(element);
				}
			});
			return [...newSelectionFlags, ...selectedFlags];
		},
		computeAllSelectedRows() {
			const newRowsData = this.invoice.expenseItem.map((item) => {
				if (this.isItemSelected(item)) {
					const newItem = _.cloneDeep(item);
					if (this.selectionData.costCode.category > -1) {
						newItem.category = this.selectionData.costCode.category;
						newItem.costCenter = this.selectionData.costCode.costCenter;
						newItem.categoryName = this.selectionData.costCode.categoryName;
					}
					if (this.selectionData.taxeFlags.length > 0) {
						const { newVat, newTotal } = this.computeSelectionPrice(item, this.selectionData.taxeFlags);
						newItem.amountVat = newVat;
						newItem.amountTotal = newTotal;
						newItem.projectFlagItems.taxeFlags = this.selectionData.taxeFlags;
					}
					if (this.selectionData.customFlags.length > 0)
						newItem.projectFlagItems.customFlags = this.computeSelectionFlags(item.projectFlagItems.customFlags, this.selectionData.customFlags);
					return newItem;
				} else {
					return item;
				}
			});
			this.invoice.expenseItem = _.cloneDeep(newRowsData);
			this.clearSelection();
			this.$refs.invoiceItemsTable.refresh();
		},
		flagFields() {
			let flags = [];
			for (let i = 0; i < this.customFlagsInvoice.length; i++) {
				flags.push({
					key: generateSecureId(this.customFlagsInvoice[i].id + '-' + i),
					label: this.customFlagsInvoice[i].short.toUpperCase(),
					sortable: true,
					class: 'text-center vertical-centered py-0',
					thStyle: { color: '#fff', 'min-width': '80px' },
					formatter: (value, key, item) => {
						if (item.projectFlagItems && item.projectFlagItems.customFlags) {
							const customFlag = item.projectFlagItems.customFlags.filter(
								(item) => !_.isNil(item) && +this.customFlagsInvoice[i].flagId === +item.flagId
							);

							if (customFlag.length > 0) {
								return customFlag[0].short.toUpperCase();
							}
							return '';
						}
						return '';
					}
				});
			}
			return flags;
		},
		provideActions(actionId) {
			switch (actionId) {
				case 1:
					return 'S';
				case 2:
					return 'I';
				case 3:
					return 'V';

				default:
					return 'C';
			}
		},
		/**
		 * @param {Array} images
		 */
		previewFile(fileObj) {
			if (isNil(fileObj.xid)) return;

			const viewerUrl = `${process.env.BASE_URL}/#/invoice-files-viewer/${INVOICE_ATTACHEMENTS_TYPE.INVOICE}/${this.invoice.id}?doc=${fileObj.xid}.${fileObj.extension}&label=${fileObj.file.name}`;
			this.openNewWindowTab(viewerUrl);
		},
		removeFile(fileObj, index) {
			const action = async () => {
				try {
					const response = await this.$axios.$get(`/removeimage/${fileObj.xid}.${fileObj.extension}`);
					if (ERROR_STRING.ITEM_NOT_FOUND === response)
						this.createToastForMobile(this.FormMSG(40, 'Error'), this.FormMSG(39, 'File not found'), '', 'danger');
					this.invoice.filesData.splice(index, 1);
					const fileExistInTemp = this.invoice.tempFilesData.findIndex((x) => x.xid === fileObj.xid);
					if (fileExistInTemp !== -1) this.invoice.tempFilesData.splice(fileExistInTemp, 1);
					this.afterAttachementUpdate = true;
					this.$emit('file:remove', fileObj);
				} catch (error) {
					this.createToastForMobile(this.FormMSG(40, 'Error'), this.FormMSG(41, 'Something went wrong'), '', 'danger');
				}
			};

			this.confirmModal(action, this.FormMSG(42, 'Are you sure to remove this file ?'), 'danger');
		},
		async deleteTempFiles() {
			if (this.invoice.tempFilesData && this.invoice.tempFilesData.length > 0) {
				for (const file of this.invoice.tempFilesData) {
					await this.$axios.$get(`/removeimage/${file.xid}.${file.extension}`);
				}
			}
		},
		openFileUploader() {
			this.openAttachementDialog = true;
		},
		closeAttachementDialog() {
			this.openAttachementDialog = false;
			this.newAttachementList = [];
		},
		onNewAttachements(newList) {
			this.newAttachementList.push(...newList);
		},
		onRemoveNewAttachement(index) {
			this.newAttachementList.splice(index, 1);
		},
		async saveFile(fileData) {
			const filesData = await provideFileDataToUpload(fileData.file);
			let formData = new FormData();
			formData.append('uploadimage', filesData.file);
			formData.append('parentType', this.invoiceParentType);
			formData.append('parentSubType', this.invoiceParentSubType);
			formData.append('parentId', +this.invoice.id);
			formData.append('fileName', filesData.name);
			formData.append('additionalDocumentType', +fileData.type);

			try {
				const urlStr = process.env.VUE_APP_GQL + '/upload';
				const savedFilesXid = await this.$axios.$post(urlStr, formData, {
					headers: {
						Authorization: `Bearer ${store.state.token}`,
						'content-type': 'multipart/form-data'
					}
				});

				return savedFilesXid.replace(/\.[^\.]*$/, '');
			} catch (error) {
				console.error('Error on upload files >>>>', error);
				return null;
			}
		},
		async handleFiles() {
			for (const file of this.newAttachementList) {
				const returnXid = await this.saveFile(file);
				const newData = {
					...file,
					xid: returnXid
				};
				this.invoice.filesData.push(newData);
				this.invoice.tempFilesData.push(newData);
				this.afterAttachementUpdate = true;
				this.$emit('file:uploaded', newData);
			}
		},
		async saveAttachements() {
			try {
				await this.showLoader(this.handleFiles, 'default-container', false);
			} catch (error) {
				console.log('Error on save attachments >>>>', error);
			} finally {
				this.closeAttachementDialog();
			}
		},
		checkIfInvoiceRejected() {
			return [VALIDATION_TYPE.REFUSED_LEVEL_1, VALIDATION_TYPE.REFUSED_LEVEL_2].includes(this.invoice.validated);
		},
		changeSelectedPoList(poID, newData) {
			this.invoice.expenseItem = _.cloneDeep(this.invoice.expenseItem.filter((item) => +item.poId !== +poID));
			this.invoice.selectedPo = _.cloneDeep(newData);
			this.$refs.invoiceItemsTable.refresh();
		},
		handleDepartmentChange(payload) {
			this.invoice.department = +payload.department;
		},
		handleLineCategoryChange(payload, index) {
			const newInvoiceExpenseItem = {
				...this.invoice.expenseItem[index],
				category: payload.category,
				costCenter: !isNil(payload.category) ? payload.costcodeText : this.invoice.expenseItem[index].costCenter,
				categoryName: !isNil(payload.category) ? payload.categoryText : this.invoice.expenseItem[index].categoryName
			};

			this.invoice.expenseItem[index] = _.cloneDeep(newInvoiceExpenseItem);
			// refresh table data.
			this.$refs.invoiceItemsTable.refresh();
		},
		handleLineCustomFlagChange(id, currentFlag, indexRow) {
			// Find the new item based on the selected id
			const newItem = currentFlag.projectFlagItem.find((item) => +item.id === +id);

			// Retrieve the customFlags array for the specific row
			const customFlags = _.cloneDeep(this.invoice.expenseItem[indexRow].projectFlagItems.customFlags);

			// Find the index of the existing flag in the customFlags array
			const index = customFlags.findIndex((selectedFlag) => +selectedFlag.flagId === +currentFlag.flagId);

			if (newItem) {
				if (index === -1) {
					// Add new item using Vue.set for reactivity
					this.$set(customFlags, customFlags.length, newItem);
				} else {
					// Update existing item using Vue.set for reactivity
					this.$set(customFlags, index, newItem);
				}
			} else {
				// If the selected ID is null, remove the item using Vue.delete
				if (index !== -1) {
					this.$delete(customFlags, index);
				}
			}

			// Force Vue to detect the change in the array
			this.$set(this.invoice.expenseItem[indexRow].projectFlagItems, 'customFlags', customFlags);

			// refresh table data.
			this.$refs.invoiceItemsTable.refresh();
		},
		onUnselected() {
			this.invoice.supplierId = 0;
			this.invoice.supplierName = '';
		},
		handleSupplierChange(supplier) {
			if (isNil(supplier)) return;
			this.invoice.supplierId = +supplier.id;
			this.invoice.supplierName = supplier.name;
		},
		openContextWizard(item, index, event) {
			if (this.canEdit) {
				event.preventDefault();
				// calculate context wizard position.
				const contextMenuWidth = this.$screen.width * (25 / 100);
				const contextMenuHeight = this.$screen.height * (50 / 100);
				this.wizardPositionY = event.clientY + contextMenuHeight > this.$screen.height ? this.$screen.height - contextMenuHeight : event.clientY;
				this.wizardPositionX = event.clientX + contextMenuWidth > this.$screen.width ? this.$screen.width - contextMenuWidth : event.clientX;
				// setup default context wizard content.
				if (this.selectedItems.length <= 1) {
					this.selectedItems[0] = _.cloneDeep(item);
					this.selectionData = _.cloneDeep({
						costCode: {
							category: +item.category,
							costCenter: item.costCenter,
							categoryName: item.categoryName
						},
						taxeFlags: item.projectFlagItems.taxeFlags,
						customFlags: item.projectFlagItems.customFlags
					});
				} else {
					this.selectionData = _.cloneDeep(DEFAULT_SELECTION_DATA);
				}
				// show the context wizard
				this.showContextWizard = true;
			}
		},
		itemRowClicked(elem, index) {
			if (this.canEdit) this.showLineItemDialog(elem, index);
		},
		itemRowDelete() {
			if (this.canEdit) this.$emit('delete:item', this.itemToDelete, this.itemToDeleteIndex);
			this.hideConfirmRowDelete();
			this.$refs.invoiceItemsTable.refresh();
		},
		showConfirmRowDelete(elem, index) {
			const action = async () => {
				this.itemToDelete = elem;
				this.itemToDeleteIndex = index;
				this.itemRowDelete();
			};

			const reset = async () => {
				this.hideConfirmRowDelete();
			};

			const el = this.$createElement;
			const alertMsg = el('div', { class: 'text-danger d-flex align-items-center justify-content-center ' }, [
				el('b-icon-exclamation-circle', { style: 'width: 32px; height: 32px;' }),
				el(
					'div',
					{ class: 'font-weight-bold ml-3' },
					this.FormMSG(5123451, `If you delete this last row, the related invoice will be automatically removed.`)
				)
			]);
			const warningMsg = el('div', { class: '' }, this.FormMSG(2632134, `Are you sure to delete this row ? (This action is irreversible)`));
			const msg = this.invoice.expenseItem.length === 1 ? [alertMsg, warningMsg] : [warningMsg];

			const msgNode = el('div', { class: '' }, msg);

			this.confirmModal(action, msgNode, 'danger', 'Please confirm', reset);
		},
		hideConfirmRowDelete() {
			this.itemToDelete = {};
			this.itemToDeleteIndex = null;
		},
		roundNumeric(value) {
			if (isNil(value) || isNaN(value)) return 0;
			value = parseFloat(value).toFixed(2);
			value = value.replace(/([\.,]0{2})$/g, '');

			return value;
		},
		setCurrency(value) {
			return rendCurrency(value);
		},
		getCustomFlagList(flag) {
			let customFlag = this.customFlagsInvoice.find((x) => {
				return x.short.toUpperCase() === flag.label;
			});
			return customFlag.projectFlagItem;
		},
		handleMergedItems() {
			const action = async () => {
				await this.restoreOrMergeItems();
			};
			this.confirmModal(action, this.FormMSG(60, 'Are you sure to carry out this operation ?'), 'danger');
		},
		showPoDialog(isShown) {
			this.poDialogInfo = {
				title: this.dialogTitlePoDrawdown,
				closeLabel: this.dialogCancel,
				saveLabel: this.dialogTitlePoNext
			};
			this.openPoDialog = isShown;
			this.showPoDrawDown = true;
			this.showPoTransaction = false;
			if (!isShown) this.enableTransactionContinueBtn = false;
		},
		switchPoTransaction() {
			this.poDialogInfo = {
				title: this.dialogTitlePoTransaction,
				closeLabel: this.dialogCancel,
				saveLabel: this.dialogTitlePoContinue
			};
			this.showPoDrawDown = false;
			this.showPoTransaction = true;
		},
		switchPoDrawDown() {
			this.poDialogInfo = {
				title: this.dialogTitlePoDrawdown,
				closeLabel: this.dialogCancel,
				saveLabel: this.dialogTitlePoNext
			};
			this.showPoDrawDown = true;
			this.showPoTransaction = false;
		},
		async nextOrSaveProcessPo() {
			if (this.showPoDrawDown) {
				this.tempPoItemsList = this.invoice.expenseItem.filter((item) => this.tempPoList.map((x) => +x.id).includes(+item.poId));
				await this.showLoader(this.switchPoTransaction, 'default-container', false);
			} else if (this.showPoTransaction) {
				// set new selection for po to the invoice
				const currentPoList = this.invoice.selectedPo.map((x) => +x.id);
				this.tempPoList.forEach((selected) => {
					if (!currentPoList.includes(+selected.id)) {
						this.invoice.selectedPo.push(selected);
					}
				});
				// set new selection for po-items to the invoice
				const reducedPoItemsList = this.invoice.expenseItem.map((x) => x.poItemId);
				this.tempPoItemsList.forEach((newSelection) => {
					if (!reducedPoItemsList.includes(newSelection.poItemId)) {
						this.invoice.expenseItem.push(newSelection);
					}
				});
				this.$refs.invoiceItemsTable.refresh();
				// close po transaction dialog.
				await this.showLoader(this.showPoDialog, 'default-container', false, false);
			}
		},
		async cancelOrCloseProcessPo() {
			if (this.showPoDrawDown) {
				this.tempPoList = [];
				await this.showLoader(this.showPoDialog, 'default-container', false, false);
				this.$refs.invoiceItemsTable.refresh();
			} else if (this.showPoTransaction) {
				this.tempPoList = [];
				this.tempPoItemsList = [];
				await this.showLoader(this.switchPoDrawDown, 'default-container', false);
			}
		},
		enableContinuePoTransaction(payload) {
			this.enableTransactionContinueBtn = payload;
		},
		updateSelectedPoList(selectedRows) {
			this.tempPoList = _.cloneDeep(selectedRows);
		},
		updateSelectedItems(newItemsList) {
			this.tempPoItemsList = _.cloneDeep(newItemsList);
		},
		showLineItemDialog(item = null, index = null) {
			this.lineItemsDialogInfo = {
				title: this.titleNewLineItem,
				closeLabel: this.dialogCancel,
				saveLabel: this.dialogSave
			};
			if (item) {
				this.currentLineItem = _.cloneDeep(item);
				this.lineItemsDialogInfo.title = this.titleEditLineItem;
				this.currentLineItemIndex = index;
			}
			this.isNotSplittableItem = isNil(index);
			this.enableSaveLineItem = false;
			this.openNewLineDialog = true;
		},
		showLineErrorMessage(isShown, type = 'default') {
			this.showlineItemDialogError = isShown;
		},
		saveUpdateLineItems() {
			this.enableSaveLineItem = false;
			try {
				const itemsUpdate = this.$refs.lineItemDataView.getLineItemUpdate();
				this.afterLineItemUpdate = true;
				if (isNil(this.currentLineItemIndex)) {
					this.invoice.expenseItem.push(itemsUpdate[0]);
				} else {
					this.invoice.expenseItem.splice(this.currentLineItemIndex, 1, ...itemsUpdate);
				}
				this.$refs.invoiceItemsTable.refresh();
				this.closeLineItemView();

				// this.createToastForMobile(this.FormMSG(465452, 'Success'), this.FormMSG(2356541, 'Transaction line saved successfully.'));
				this.createToastForMobile(this.FormMSG(70, 'Info'), this.FormMSG(75, 'you need to save in order to apply your current change'), '', 'info');
			} catch (error) {
				console.log('Error on save/update line items >>>>>>>>', error);
				this.enableSaveLineItem = true;
			}
		},
		closeLineItemView() {
			this.currentLineItem = {};
			this.currentLineItemIndex = null;
			this.enableSaveLineItem = false;
			this.openNewLineDialog = false;
		}
	},
	validations() {
		return {
			invoice: {
				date: { required },
				description: {
					required,
					min: minLength(4)
				},
				amountTotal: {
					required,
					decimal
				},
				supplierId: {
					required
				},
				invoiceNumber: {
					required,
					min: minLength(2)
				}
			}
		};
	}
};
</script>

<style lang="scss">
.floating-wizard-enter-active,
.floating-wizard-leave-active {
	transition: all 0.5s ease;
}

.floating-wizard-enter-from,
.floating-wizard-leave-to {
	opacity: 0;
}

.table-no-outline-border {
	border: 0 !important;
}

.vertical-centered {
	vertical-align: middle !important;
}

.bgc-amount-total {
	background-color: #d1d5db;
}

.tr-cursor {
	cursor: pointer !important;
}

.warning-invoice {
	color: #bc9581;
}

.description-overflow {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.files-btn-background {
	background-color: rgba(226, 230, 235, 0.54);
}

.custom-sticky-header {
	position: sticky;
	top: 0;
}
.custom-sticky-footer {
	position: sticky;
	background-color: #fff;
	bottom: 0;
}

.mini-table,
.mini-table thead th {
	font-size: 0.9em !important;
	padding: 0.5em;
}

.text-red {
	color: #ea4e2c;
}

.bg-primary {
	background-color: #00a09c !important;
}

.text-description-overflow-ellypsis {
	max-height: 40px;
	overflow: hidden;
	text-overflow: ellipsis;
	display: -webkit-box;
	line-clamp: 2;
	-webkit-box-orient: vertical;
	-webkit-line-clamp: 2; /* Ensure line clamp works */
}

.z-index-3000 {
	z-index: 3000;
}

.z-index-4000 {
	z-index: 4000;
}

.custom-flag-select-class .vs__dropdown-toggle {
	border-radius: 0 !important;
}

.custom-flag-select-class .vs__dropdown-toggle .vs__selected-options {
	flex-wrap: nowrap !important;
	padding: 0 !important;
}

.custom-flag-select-class .vs__dropdown-toggle .vs__selected-options .vs__selected {
	display: inline-block;
	min-width: 0;
	max-width: 100%;
	white-space: nowrap;
}

.vc-popover-content-wrapper {
	z-index: 9999 !important;
}

.fixed-height-container {
	height: 500px;
	overflow: hidden;
}

.po-mobile-container {
	height: 100%;
	overflow: auto;
}

.list-file {
	display: flex;
	align-items: center;
	padding-inline-start: 16px;
	position: relative;
}

.list-file::before {
	content: '■'; /* Customize if you want a square or other style */
	color: rgba(6, 38, 62, 0.7); /* Set the bullet color */
	position: absolute;
	left: 2px;
	top: -1px;
}

.file-name {
	display: inline-block;
	max-width: 85%;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.invoices-description-tooltip {
	z-index: 99999;
}
</style>
